import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { refusalMessage } from '../api';
import { clientResets, riderResets } from '../api/passwordResets';
import InGoLogo from '../components/InGoLogo';
import './auth.css';
import './driverForgotPasswordPremium.css';

/** A rider's link says `?realm=driver`; anything else is a client. */
function parseRealm(raw) {
  const r = String(raw ?? 'customer').trim().toLowerCase();
  if (r === 'driver') return r;
  return 'customer';
}

export function IconBack() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15.5 18.5L8.5 12l7-7.5"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconEnvelope() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 6h16v12H4V6Zm0 0 8 7 8-7"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function IconInfo() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden className="dp-fp-info-icon">
      <circle cx="12" cy="12" r="7.2" stroke="currentColor" strokeWidth="1.4" fill="none" />
      <path d="M12 10.2V16M12 7.2v.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function isValidEmail(value) {
  const v = value.trim();
  if (!v) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export function DriverForgotPasswordLayout({ loginPath, children, foot, title = 'Forgot Password' }) {
  return (
    <div className="dp-fp-page">
      <header className="dp-fp-nav">
        <Link to={loginPath} className="dp-fp-nav-back" aria-label="Back to login">
          <IconBack />
        </Link>
        <h1>{title}</h1>
        <span className="dp-fp-nav-spacer" aria-hidden />
      </header>
      <main className="dp-fp-main">
        <div className="dp-fp-card">{children}</div>
        {foot}
      </main>
    </div>
  );
}

/**
 * "Forgot password?" for a client or a rider, on InGo's API (ingo-e0, 2 Oct
 * 2026): one step. They give their address; the server answers the same
 * whether or not it is anyone's, and mails a link to the account's own
 * address, which opens `ResetPasswordPage`.
 */
function ApiForgotPassword({ realm, resets }) {
  const [searchParams] = useSearchParams();
  const isRider = realm === 'driver';
  const loginPath = isRider ? '/driver/login' : '/login';
  const [email, setEmail] = useState(() => String(searchParams.get('email') || '').trim().toLowerCase());
  const [sent, setSent] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const ask = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (!isValidEmail(email)) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }
    setBusy(true);
    try {
      setSent(await resets.ask(email.trim().toLowerCase()));
    } catch (err) {
      setErrorMessage(refusalMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const again = () => {
    setSent('');
    setErrorMessage('');
  };

  if (isRider) {
    return (
      <DriverForgotPasswordLayout
        loginPath={loginPath}
        foot={
          <p className="dp-fp-foot">
            Remember your password? <Link to={loginPath}>Login</Link>
          </p>
        }
      >
        {sent ? (
          <>
            <h2 className="dp-fp-title">Check your email</h2>
            <p className="dp-fp-msg dp-fp-msg--success" role="status">
              {sent}
            </p>
            <div className="dp-fp-info" role="note">
              <IconInfo />
              <p className="dp-fp-info-text">
                Open the link on this phone to choose a new password. Check your spam folder if it has not arrived in a few minutes.
              </p>
            </div>
            <button type="button" className="dp-fp-btn-secondary" style={{ marginTop: '0.65rem' }} onClick={again}>
              Use a different email
            </button>
            <Link to={loginPath} className="dp-fp-btn-link">
              Back to login
            </Link>
          </>
        ) : (
          <>
            <h2 className="dp-fp-title">Forgot Password?</h2>
            <p className="dp-fp-subtitle">Enter the email you registered with and we will send you a link to choose a new password.</p>
            <form onSubmit={ask} noValidate>
              <div className="dp-fp-field">
                <label className="dp-fp-label" htmlFor="forgot-email">
                  Email address
                </label>
                <div className="dp-fp-input-wrap">
                  <span className="dp-fp-iconbox" aria-hidden>
                    <IconEnvelope />
                  </span>
                  <input
                    id="forgot-email"
                    className="dp-fp-input"
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              {errorMessage ? (
                <p className="dp-fp-msg dp-fp-msg--error" role="alert">
                  {errorMessage}
                </p>
              ) : null}
              <button type="submit" className="dp-fp-btn-primary" disabled={busy}>
                {busy ? 'Please wait…' : 'Send Reset Link'}
              </button>
            </form>
          </>
        )}
      </DriverForgotPasswordLayout>
    );
  }

  return (
    <div className="auth-page auth-page--register auth-page--forgot">
      <div className="auth-top">
        <Link to={loginPath} className="auth-back" aria-label="Back to login">
          <IconBack />
        </Link>
      </div>

      <div className="auth-card">
        <InGoLogo variant="auth" />
        {sent ? (
          <>
            <h1 className="auth-title auth-title--subhead-gap">Check your email</h1>
            <p className="auth-message auth-message--success" role="status">
              {sent}
            </p>
            <p className="auth-subtitle">Open the link to choose a new password. Check your spam folder if it has not arrived in a few minutes.</p>
            <button type="button" className="auth-btn-secondary" style={{ marginTop: '0.65rem', width: '100%' }} onClick={again}>
              Use a different email
            </button>
            <Link to={loginPath} className="auth-btn-primary auth-btn-primary--link auth-btn-primary--stacked">
              Back to login
            </Link>
          </>
        ) : (
          <>
            <h1 className="auth-title auth-title--subhead-gap">Forgot password?</h1>
            <p className="auth-subtitle">Enter the email you registered with and we will send you a link to choose a new password.</p>
            <form onSubmit={ask} noValidate>
              <div className="auth-field">
                <label className="auth-label" htmlFor="forgot-email-client">
                  Email address
                </label>
                <div className="auth-input-wrap">
                  <input
                    id="forgot-email-client"
                    className="auth-input"
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              {errorMessage ? (
                <p className="auth-message auth-message--error" role="alert">
                  {errorMessage}
                </p>
              ) : null}
              <button type="submit" className="auth-btn-primary" disabled={busy}>
                {busy ? 'Please wait…' : 'Send reset link'}
              </button>
            </form>
          </>
        )}
        <p className="auth-foot">
          Remember your password?{' '}
          <Link to={loginPath} className="auth-link-inline">
            Login
          </Link>
        </p>
      </div>
    </div>
  );
}

/** Clients and riders ask InGo's API for a link. */
export default function ForgotPasswordPage({ resets = { customer: clientResets, driver: riderResets } }) {
  const [searchParams] = useSearchParams();
  const realm = parseRealm(searchParams.get('realm'));

  return <ApiForgotPassword key={realm} realm={realm} resets={resets[realm]} />;
}


